/**
 * Resumable-upload chunk sizing. A LEAF module — no API/service imports — so a
 * contract test can exercise these against UpChunk's real validation without
 * dragging in the Firebase/axios graph that importing performUpload.ts would.
 */

/**
 * Opening chunk size (a 256 KiB multiple). UpChunk adapts up from here — see
 * DYNAMIC_CHUNK_* below — so this is a first bid, not a fixed size. Well under
 * UpChunk's own 30 MB default: a smaller first chunk finishes sooner, so the
 * adaptive sizing gets its first throughput sample earlier, and peak memory is
 * lower (UpChunk buffers each chunk whole). Progress is NOT the reason — that
 * streams continuously within every chunk regardless of size.
 */
export const CHUNK_SIZE_KB = 5 * 1024;

/**
 * Bounds for UpChunk's adaptive sizing. Read the real semantics before tuning:
 * resizing happens ONLY after a chunk that SUCCEEDED — doubling under 10 s,
 * halving over 30 s. A FAILED chunk is retried at its current size, so dynamic
 * sizing does not by itself reduce retry cost; the ceiling does.
 *
 * Hence 8 MB rather than UpChunk's ~500 MB default ceiling: on a ~5 Mbit/s
 * mobile uplink the growth rule reaches the ceiling within the first few chunks,
 * so the ceiling IS the worst-case re-send. 8 MB is ~13 s there, and caps peak
 * per-chunk memory on a constrained WebView. Google's resumable-upload guidance
 * recommends at least 8 MiB and going lower under memory pressure.
 *
 * Values are in kB and must be 256-multiples — UpChunk validates both.
 */
export const DYNAMIC_CHUNK_MIN_KB = 256;
export const DYNAMIC_CHUNK_MAX_KB = 8 * 1024;
